import { Vue, Options } from 'vue-class-component';
import {
  FloorConfigDTO,
  RiserConfigDTO,
  RFAConfigDTO,
  BuildingDTO,
  EntrancePlantDetailDTO,
  EntrancePlantStructureCreateDTO,
} from '@/../client-generator/generated-clients/plants';
import CardContainer from '@/views/components/CardContainer/CardContainer.vue';
import NumberButtons from '@/views/components/NumberButtons/NumberButtons.vue';
import plantsClient from '@/core/services/plants';
import { 
  DEFAULT_NUMBER_OF_FLOORS_IN_RISER, 
  distributeApartmentsToRaisersAndFloors 
} from '@/core/utils/EntrancePlant/entrance-plant.utils';
import { toLabel } from '@/core/utils/EntrancePlant/denomination-helper';

@Options({
  name: 'DialogEntrancePlantCreateStep4',
  components: {
    CardContainer,
    NumberButtons,
  },
  props: {
    plant: {
      required: true,
      type: Object as () => EntrancePlantDetailDTO,
    },
    plantId: {
      type: Number,
      required: false,
    },
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    plant: function (newValue) {
      if (newValue) this.initializeData();
    },
  },
  emits: ['is-loading', 'onBack', 'onNext', 'plant-edited'],
  computed: {},
})
export default class DialogEntrancePlantCreateStep4 extends Vue {
  plant!: EntrancePlantDetailDTO;
  plantId!: number;
  currentBuildingIndex = 0;
  distributeApartmentsToRaisersAndFloors = distributeApartmentsToRaisersAndFloors;

  changingBuilding = false;
  rfaConfig: { [key: string]: RFAConfigDTO } = {};
  floorsCountValues: number[] = [];

  get titleCard() {
    return `${this.risersCount} ${this.$t(
      this.risersCount == 1 ? 'AppResourceEP.Riser' : 'AppResourceEP.Risers'
    ).toString()}`;
  }

  get buildingName() {
    return this.currentBuilding?.name;
  }

  get currentBuilding(): BuildingDTO | null {
    if (this.plant.buildings) {
      return this.plant.buildings[this.currentBuildingIndex];
    }
    return null;
  }

  get currentRFAConfig(): RFAConfigDTO | null {
    if (!this.currentBuilding) return null;
    if (!this.currentBuilding.buildingId) return null;
    return this.rfaConfig[this.currentBuilding.buildingId];
  }

  get risersCount(): number {
    if (!this.currentRFAConfig) return 0;
    if (!this.currentRFAConfig.risers) return 0;
    return this.currentRFAConfig.risers.length;
  }

  get apartmentCountValues(): { label: number; value: number }[] {
    if (!this.currentBuilding?.apartments) return [{ label: 0, value: 0 }];
    return [...Array(this.currentBuilding?.apartments?.length + 1).keys()].map(
      (i) => {
        return { label: i, value: i };
      });
  }

  get mismatchedApartmentsErrorText() {
    const firstLine = this.$t('entrancePlant.buildingApartments') + ': ' + this.currentBuilding?.apartments?.length;
    const secondLine = this.$t('entrancePlant.assignedApartmets') + ': ' + this.totalAssignedApartments;
    return [firstLine, secondLine];
  }

  get totalAssignedApartments() {
    if (!this.currentRFAConfig || !this.currentRFAConfig.risers) return 0;
    return this.currentRFAConfig.risers.reduce(
      (acc, riser) =>
        acc +
        riser.floors!.reduce(
          (acc, floor) => acc + (floor.numberOfApartments ?? 0),
          0
        ),
      0
    );
  }

  get allApartmentsAssigned() {
    if (!this.currentRFAConfig || !this.currentRFAConfig.risers) return false;
    return this.currentRFAConfig.numberOfApartments === this.totalAssignedApartments;
  }

  riserName(index?: number): string {
    if (!index) return '';
    return `${this.$t('AppResourceEP.Name')} ${this.$t(
      'AppResourceEP.Riser'
    )} ${toLabel(index, this.plant.denominationRisers)}`;
  }

  floorName(index?: number): string {
    if (!index) return '';
    return `${this.$t('AppResourceEP.Name')} ${this.$t(
      'AppResourceEP.Floor'
    )} ${toLabel(index, this.plant.denominationFloors)}`;
  }

  changeFloorNumber(riser: RiserConfigDTO, value: number) {
    if (!this.currentRFAConfig) return;
    if (!riser.floors) riser.floors = [];
    const count = riser.floors.length - value;
    if (count < 0) {
      this.addFloors(riser, Math.abs(count));
    } else {
      this.removeFloors(riser, count);
    }
    this.distributeApartmentsToRaisersAndFloors(this.currentRFAConfig);
  }

  changeApartmentNumber(floor: FloorConfigDTO, value: number) {
    if (!this.currentRFAConfig) return;
    floor.numberOfApartments = value;
  }

  addRiser() {
    if (!this.currentRFAConfig) return;
    if (!this.currentRFAConfig.risers) this.currentRFAConfig.risers = [];

    const riserName = `${this.$t('AppResourceEP.Riser')} ${toLabel(
      this.risersCount + 1,
      this.plant.denominationRisers
    )}`;

    const riser = new RiserConfigDTO({
      index: this.risersCount + 1,
      name: riserName,
    });

    this.addFloors(riser, DEFAULT_NUMBER_OF_FLOORS_IN_RISER);

    this.currentRFAConfig.risers.push(riser);
    this.distributeApartmentsToRaisersAndFloors(this.currentRFAConfig);
  }

  addFloors(riser: RiserConfigDTO, floorsCount: number) {
    if (!riser.floors) riser.floors = [];

    for (let index = 0; index < floorsCount; index++) {
      const floorIndex = riser.floors.length + 1;
      const floorName = `${this.$t('AppResourceEP.Floor')} ${toLabel(
        floorIndex,
        this.plant.denominationFloors
      )}`;

      riser.floors.push(
        new FloorConfigDTO({
          index: floorIndex,
          name: floorName,
        })
      );
    }
  }

  removeFloors(riser: RiserConfigDTO, floorsCount: number) {
    if (!riser.floors) return;
    riser.floors.splice(riser.floors.length - floorsCount, floorsCount);
  }

  removeRiser() {
    if (!this.currentRFAConfig) return;
    if (!this.currentRFAConfig.risers) return;
    this.currentRFAConfig.risers.splice(
      this.currentRFAConfig.risers.length - 1,
      1
    );
    this.distributeApartmentsToRaisersAndFloors(this.currentRFAConfig);
  }

  mounted(): void {
    this.initializeData();
  }

  initializeData() {
    this.floorsCountValues = [
      ...Array(200).keys(),
    ].map((i) => i + 1);
    if (this.plant.buildings) {
      for (const building of this.plant.buildings) {
        if (!building.buildingId) continue;
        if (!building.apartments) continue;
        if (building.risers == null || building.risers.length == 0) {
          this.rfaConfig[building.buildingId] = new RFAConfigDTO({
            numberOfApartments: building.apartments.length,
            risers: [
              new RiserConfigDTO({
                index: 1,
                name: `${this.$t('AppResourceEP.Riser')} ${toLabel(
                  1,
                  this.plant.denominationRisers
                )}`,
              }),
              new RiserConfigDTO({
                index: 2,
                name: `${this.$t('AppResourceEP.Riser')} ${toLabel(
                  2,
                  this.plant.denominationRisers
                )}`,
              }),
            ],
          });
          this.addFloors(this.rfaConfig[building.buildingId].risers![0], DEFAULT_NUMBER_OF_FLOORS_IN_RISER);
          this.addFloors(this.rfaConfig[building.buildingId].risers![1], DEFAULT_NUMBER_OF_FLOORS_IN_RISER);
          distributeApartmentsToRaisersAndFloors(this.rfaConfig[building.buildingId]);
        }
        else {
          var rfaConfig = new RFAConfigDTO({
            numberOfApartments: building.apartments.length,
            risers: [],
          });
          building.risers.forEach((riser) => {
            rfaConfig.risers?.push(
              new RiserConfigDTO({
                riserId: riser.riserId,
                index: Number.parseInt(riser.index!),
                name: riser.name,
                floors: building.floors?.filter(r=>r.riserId==riser.riserId).map((floor) => {
                  return new FloorConfigDTO({
                    index: Number.parseInt(floor.index!),
                    name: floor.name,
                    numberOfApartments:building.apartments?.filter(a=>a.floorId==floor.floorId).length,
                  });
                }),
              }),
            );
          });
          this.rfaConfig[building.buildingId] = rfaConfig;
        }
      }
    }
  }

  getDataToSave() {
    return new EntrancePlantStructureCreateDTO({
      wizardStep: 4,
      plantConfigStructure: this.rfaConfig,
    });
  }

  handleBackButton() {
    if (this.plant.buildings) {
      if (this.currentBuildingIndex > 0) {
        this.changeBuilding('back');
        return;
      }
    }
    this.$emit('onBack', 'DialogEntrancePlantCreateStep3');
  }

  async handleNextButton() {
    if (this.plant.buildings) {
      if (this.currentBuildingIndex < this.plant.buildings.length - 1) {
        await this.changeBuilding('next');
        return;
      }
      await this.updatePlantStructure(this.getDataToSave());
    }
    this.$emit('onNext', 'DialogEntrancePlantCreateStep5');
  }

  async changeBuilding(direction: 'next' | 'back') {
    this.$emit('is-loading', true);
    this.changingBuilding = true;
    if (direction === 'next') {
      this.currentBuildingIndex++;
    } else {
      this.currentBuildingIndex--;
    }
    setTimeout(() => {
        this.changingBuilding = false;
        this.$emit('is-loading', false)
      }, 500);
  }

  async updatePlantStructure(structure: EntrancePlantStructureCreateDTO) {
    if (!this.plantId) return;
    this.$emit('is-loading', true);

    try {
      await plantsClient.structure(this.plant.desktopId!, structure);
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 6000,
      });
    } finally {
      this.$emit('is-loading', false);
    }
  }
}
